/**
 * UX-2 world-card styles, injected once. Design tokens follow the UX-1 kit:
 * cream cards, charcoal terminal, status pills, phase progress bar segments.
 */
let injected = false

export const CARD_TIERS = { FULL: 8, CHIP: 20 } as const

export type LabelTier = 'full' | 'chip' | 'hidden'

/** Distance → render tier for a station card. */
export function labelTierFor(dist: number): LabelTier {
  if (dist <= CARD_TIERS.FULL) return 'full'
  if (dist <= CARD_TIERS.CHIP) return 'chip'
  return 'hidden'
}

export type SegmentState = 'done' | 'active' | 'todo' | 'failed'

/** Phase list → progress-bar segment states. */
export function phaseSegments(
  phases: Array<{ status: string }>,
): SegmentState[] {
  return phases.map((p) => {
    if (p.status === 'completed' || p.status === 'success') return 'done'
    if (p.status === 'running') return 'active'
    if (p.status === 'failed') return 'failed'
    return 'todo'
  })
}

export function injectWorldCardStyles(): void {
  if (injected || typeof document === 'undefined') return
  injected = true
  const style = document.createElement('style')
  style.id = 'world-card-styles'
  style.textContent = `
.wcard {
  font-family: 'Nunito', system-ui, sans-serif;
  background: rgba(245, 235, 224, 0.94);
  color: #3d405b;
  border-radius: 10px;
  padding: 8px 12px;
  box-shadow: 0 4px 16px rgba(61, 64, 91, 0.28);
  text-align: center;
  min-width: 150px;
  max-width: 300px;
}
.wcard-title {
  font-weight: 800;
  font-size: 13px;
  line-height: 1.25;
  color: #3d405b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 280px;
}
.wcard-meta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 3px;
  font-size: 10.5px;
  color: #6b6f8a;
}
.wcard-pill {
  display: inline-block;
  padding: 1px 7px;
  border-radius: 999px;
  font-weight: 800;
  font-size: 9.5px;
  letter-spacing: 0.04em;
  color: #fff;
}
.wcard-pill--running   { background: #a78bfa; }
.wcard-pill--completed { background: #22d3ee; }
.wcard-pill--failed    { background: #f87171; }
.wcard-pill--queued,
.wcard-pill--pending   { background: #94a3b8; }
.wcard-pill--cancelled { background: #6b7280; }
.wcard-bar {
  display: flex;
  gap: 3px;
  margin-top: 6px;
}
.wcard-seg {
  flex: 1;
  height: 5px;
  border-radius: 3px;
  background: rgba(61, 64, 91, 0.15);
}
.wcard-seg--done   { background: #22d3ee; }
.wcard-seg--failed { background: #f87171; }
.wcard-seg--active {
  background: #a78bfa;
  animation: wcard-pulse 1.1s ease-in-out infinite;
}
@keyframes wcard-pulse {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.45; }
}
.wcard-term {
  margin-top: 7px;
  background: #2f3247;
  color: #cbd5e1;
  border-radius: 7px;
  padding: 5px 8px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 9.5px;
  line-height: 1.45;
  text-align: left;
  max-width: 284px;
  overflow: hidden;
}
.wcard-term .t-tool { color: #fbbf24; }
.wcard-kbd-row {
  margin-top: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-size: 10.5px;
  font-weight: 800;
  color: #e07a5f;
  animation: wcard-bob 1.4s ease-in-out infinite;
}
.wcard-kbd {
  display: inline-block;
  background: #3d405b;
  color: #f5ebe0;
  border-radius: 5px;
  padding: 1px 7px;
  font-size: 10px;
  font-weight: 800;
  box-shadow: 0 2px 0 rgba(61, 64, 91, 0.5);
}
@keyframes wcard-bob {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-2px); }
}
.wchip {
  font-family: 'Nunito', system-ui, sans-serif;
  display: flex;
  align-items: center;
  gap: 5px;
  background: rgba(61, 64, 91, 0.78);
  color: #f5ebe0;
  border-radius: 999px;
  padding: 2px 9px;
  font-size: 10px;
  font-weight: 700;
  max-width: 180px;
  white-space: nowrap;
}
.wchip-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex: none;
}
.wchip-title { overflow: hidden; text-overflow: ellipsis; }
`
  document.head.appendChild(style)
}
